import React, { FC, RefObject } from 'react';
import { ScrollView, View } from 'react-native';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { isPhone } from '@helpers';
import ColorWrapper from '@styles/colors';
import type { WithColor } from '@styles/colors';
import type { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ImagesContainerMode, type ImagesContainerProps } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import type { Machine } from '@interfaces/machines';

import { AddImageButton } from '@components/machines/components/detailsComponents/activeMachine/components/Images/components/AddImageButton';
import { FlatLineAddImage } from '@components/machines/components/detailsComponents/activeMachine/components/Images/components/FlatLineAddImage';
import { ImagesRender } from '@components/machines/components/detailsComponents/activeMachine/components/Images/components/ImagesRender';
import { MachineImage } from '@components/machines/components/detailsComponents/activeMachine/components/Images/components/MachineImage';
import { ThumbnailNavButton } from '@components/machines/components/detailsComponents/activeMachine/components/Images/components/ThumbnailNavButton';
import { useManageImages } from '@components/machines/components/detailsComponents/activeMachine/components/Images/hooks/useManageImages';
import type { ImageItem } from '@components/machines/components/detailsComponents/activeMachine/components/Images/types';

export interface ImagesProps
  extends WithColor,
    Pick<
      ImagesContainerProps,
      | 'documentRetrieveKey'
      | 'documentRetrieveId'
      | 'panelViewMode'
      | 'isImageHandlingNavigation'
      | 'onlySelectImage'
      | 'uploadImage'
      | 'largeAddView'
      | 'flatLineBorderRadiusConfig'
      | 'bg'
      | 'onImagePress'
      | 'preselectedScopeIds'
      | 'machineId'
    > {
  machine?: Machine;
  images: ImageItem[];
  imageScroll?: RefObject<ScrollView>;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  shouldDisplayFavorites?: boolean;
  mode?: ImagesContainerMode;
}

const Images: FC<ImagesProps> = (props) => {
  const {
    C,
    machineId,
    documentRetrieveKey,
    documentRetrieveId,
    images,
    imageScroll,
    isImageHandlingNavigation = true,
    panelViewMode,
    scale = initialScale,
    font = initialFont,
    flatLineBorderRadiusConfig,
    bg,
    onImagePress,
    shouldDisplayFavorites,
    mode,
  } = props;

  const {
    machineImageLink,
    shouldDisplayMachineImage,
    noImages,
    showFlatLine,
    machineImageData,
    upload,
  } = useManageImages(props);

  const renderMachineOrImages = (passOnImagePressToMachine: boolean) =>
    shouldDisplayMachineImage ? (
      <MachineImage
        C={C}
        imageLink={machineImageLink}
        imageRandomHash={machineImageData.imageRandomHash}
        imageData={machineImageData.imageData}
        imageRotationAngle={machineImageData.imageRotationAngle}
        imageDocumentId={machineImageData.machineDocumentId}
        isImageHandlingNavigation={isImageHandlingNavigation}
        machineId={machineId}
        panelViewMode={panelViewMode}
        scale={scale}
        onImagePress={passOnImagePressToMachine ? onImagePress : undefined}
      />
    ) : (
      <ImagesRender
        C={C}
        images={images}
        isImageHandlingNavigation={isImageHandlingNavigation}
        documentRetrieveKey={documentRetrieveKey}
        documentRetrieveId={documentRetrieveId}
        panelViewMode={panelViewMode}
        scale={scale}
        onImagePress={onImagePress}
        shouldDisplayFavorites={shouldDisplayFavorites}
        mode={mode}
      />
    );

  if (showFlatLine) {
    return (
      <FlatLineAddImage
        C={C}
        flatLineBorderRadiusConfig={flatLineBorderRadiusConfig}
        bg={bg}
        scale={scale}
        onPress={upload}
      />
    );
  }

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <ScrollView
        horizontal={!panelViewMode}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[{ minWidth: '100%', paddingLeft: scale(panelViewMode || noImages ? 0 : 15) }]}
        ref={imageScroll}
      >
        <View
          style={[
            !panelViewMode && { height: scale(isPhone && noImages ? 42 : 98) },
            {
              width: '100%',
              flexDirection: panelViewMode ? 'column' : 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
            },
          ]}
        >
          <AddImageButton
            C={C}
            panelViewMode={panelViewMode}
            noImages={noImages}
            machineId={machineId}
            documentRetrieveKey={documentRetrieveKey}
            mode={mode}
            bg={bg}
            scale={scale}
            font={font}
            onPress={upload}
          />

          {panelViewMode && renderMachineOrImages(false)}

          {!panelViewMode && <View style={{ flex: 1, flexDirection: 'row' }}>{renderMachineOrImages(true)}</View>}
        </View>
      </ScrollView>

      {!panelViewMode && isImageHandlingNavigation && images?.length > 1 && (
        <ThumbnailNavButton documentRetrieveKey={documentRetrieveKey} documentRetrieveId={documentRetrieveId} scale={scale} />
      )}
    </View>
  );
};

export default ColorWrapper(Images);
